import "./ObjectSettings.style.css";

import React, { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Property } from "./Property/Property";
import { ObjectState } from "../../../utils/states/ConstructorPageStates/ObjectState";
import { PropertyState } from "../../../utils/states/ConstructorPageStates/PropertyState";
import { UniqueId } from "../../../utils/UniqueId";
import { Status } from "../../../utils/enums/Status";
import { MAX_FILE_SIZE_MB } from "../../../utils/constants/constants";

import deleteImage from "./../../../resources/images/delete.png";
import copyImage from "./../../../resources/images/copy.png";

interface ObjectSettingsProps {
  editedObject: ObjectState;
  setObjects: Dispatch<SetStateAction<ObjectState[]>>;
  setEditedObject: React.Dispatch<React.SetStateAction<ObjectState | null>>;
  handleChangeObject: (newObject: ObjectState) => void;
  properties: PropertyState[];
  objectSettingsRef: React.RefObject<HTMLDivElement | null>;
  handleValueChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const ObjectSettings = ({
  editedObject,
  setObjects,
  setEditedObject,
  properties,
  handleChangeObject,
  objectSettingsRef,
  handleValueChange,
}: ObjectSettingsProps) => {
  const handleChangeObjectName = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "objectName") {
      const newObject = {
        ...editedObject,
        status: editedObject.apiId ? Status.Update : editedObject.status,
        name: value,
      };

      handleChangeObject(newObject);
    }
  };

  const handleCopyObject = () => {
    const newObject = {
      ...editedObject,
      apiId: null,
      status: Status.Create,
      id: UniqueId.generateUniqueId(),
    };

    setObjects((prevState) => {
      const index = prevState.findIndex(
        (object) => editedObject.id === object.id
      );

      if (index === -1) {
        return [...prevState, newObject];
      }

      return [
        ...prevState.slice(0, index + 1),
        newObject,
        ...prevState.slice(index + 1),
      ];
    });

    setEditedObject(newObject);
  };

  const handleDeleteObject = () => {
    let isPrevious = true;
    let isNext = false;
    let newEditedObject: ObjectState | null = null;

    if (editedObject.apiId) {
      setObjects((prevState) =>
        prevState.map((object, index) => {
          if (object.id === editedObject.id) {
            if (index < prevState.length - 1) {
              isPrevious = false;
              isNext = true;
            }

            return {
              ...object,
              status: Status.Delete,
            };
          }

          if (isPrevious) {
            newEditedObject = object;
          }

          if (isNext) {
            isNext = false;
            newEditedObject = object;
          }

          return object;
        })
      );
    } else {
      setObjects((prevState) =>
        prevState.filter((object, index) => {
          if (object.id === editedObject.id) {
            if (index < prevState.length - 1) {
              isPrevious = false;
              isNext = true;
            }

            return false;
          }

          if (isPrevious) {
            newEditedObject = object;
          }

          if (isNext) {
            isNext = false;
            newEditedObject = object;
          }

          return true;
        })
      );

      UniqueId.deleteUniqueId(editedObject.id);
    }

    setEditedObject(newEditedObject);
  };

  const handleObjectImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      console.error("Слишком большой файл");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result as string;
      setObjects((prevObjects) =>
        prevObjects.map((object) =>
          object.id === editedObject.id
            ? {
                ...object,
                image: result,
                status: object.apiId ? Status.Update : object.status,
              }
            : object
        )
      );

      setEditedObject((prevState) => {
        if (!prevState) {
          return null;
        }

        return {
          ...prevState,
          image: result,
          status: prevState.apiId ? Status.Update : prevState.status,
        };
      });
    };

    reader.onerror = () => {
      console.error("Не получилось прочитать файл");
    };

    reader.readAsDataURL(file);
  };

  const handlePropertyImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const { name } = e.target;

    const [objectId, propertyId] = name.split("_").map((id) => parseInt(id));

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      console.error("Слишком большой файл");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result as string;
      setObjects((prevObjects) => {
        return prevObjects.map((obj) => {
          if (obj.id === objectId) {
            const objectValue = obj.properties[propertyId];

            return {
              ...obj,
              properties: {
                ...obj.properties,
                [propertyId]: { type: objectValue.type, value: result },
              },
              status: obj.apiId ? Status.Update : obj.status,
            };
          }

          return obj;
        });
      });

      setEditedObject((prevState) => {
        if (!prevState) {
          return null;
        }

        const objectValue = prevState.properties[propertyId];

        return {
          ...prevState,
          properties: {
            ...prevState.properties,
            [propertyId]: { type: objectValue.type, value: result },
          },
          status: prevState.apiId ? Status.Update : prevState.status,
        };
      });
    };

    reader.onerror = () => {
      console.error("Не получилось прочитать файл");
    };

    reader.readAsDataURL(file);
  };

  return (
    <div ref={objectSettingsRef} className="object_settings_container">
      <div className="object_settings__title header_3">Настройка объекта</div>
      <div className="object_settings__input_block header_4">
        Название
        <input
          autoComplete="off"
          onChange={handleChangeObjectName}
          name="objectName"
          className="object_settings__input text_2"
          value={editedObject.name}
        />
      </div>

      <div className="object_settings__image_block text_3">
        <div className="object_settings__image_title header_4">Картинка</div>
        <label
          className="object_settings__image"
          htmlFor="imageUploadInput"
          style={{ cursor: "pointer", display: "block" }}
        >
          <img src={editedObject.image} alt="Изображение для загрузки" />
        </label>
        <input
          autoComplete="off"
          id="imageUploadInput"
          className="card_settings__image_input"
          type="file"
          accept="image/*"
          onChange={handleObjectImageUpload}
        />
        Рекомендуемое разрешение: 240x240
      </div>
      <div className="object_settings__properties">
        {properties.map((property) => (
          <Property
            propertyName={property.name}
            objectValue={editedObject.properties[property.id]}
            handleChangeValue={handleValueChange}
            inputName={`${editedObject.id}_${property.id}`}
            handlePropertyImageUpload={handlePropertyImageUpload}
            key={property.id}
          />
        ))}
      </div>
      <div className="object_settings__buttons">
        <button
          onClick={handleCopyObject}
          className="object_settings__copy_button"
        >
          <img src={copyImage} alt="copy button" />
        </button>
        <button
          onClick={handleDeleteObject}
          className="object_settings__delete_button"
        >
          <img src={deleteImage} alt="delete button" />
        </button>
      </div>
    </div>
  );
};
